import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from './ui/button';
import { Navigation, Phone, MapPin, ExternalLink } from 'lucide-react';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to recenter map when coordinates change
function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom || 15);
    }
  }, [center, zoom, map]);
  return null;
}

// Generate Google Maps navigation URL
export function getGoogleMapsUrl(lat, lng, address) {
  if (lat && lng) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  } else if (address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  }
  return null;
}

// Generate Apple Maps navigation URL (for iOS)
export function getAppleMapsUrl(lat, lng, address) {
  if (lat && lng) {
    return `https://maps.apple.com/?daddr=${lat},${lng}`;
  } else if (address) {
    return `https://maps.apple.com/?daddr=${encodeURIComponent(address)}`;
  }
  return null;
}

// Main MapPreview component
export default function MapPreview({ 
  location, 
  address,
  customerName,
  customerPhone,
  showNavigation = true,
  showCallButton = true,
  height = '200px',
  className = '',
  onConfirmLocation,
  draggable = false,
  onLocationChange
}) {
  const markerRef = useRef(null);
  
  // Extract coordinates
  const lat = location?.lat || location?.latitude;
  const lng = location?.lng || location?.longitude;
  const hasCoordinates = lat && lng;
  
  // Default center (US center) if no coordinates
  const center = hasCoordinates ? [lat, lng] : [39.8283, -98.5795];
  const zoom = hasCoordinates ? 15 : 4;

  const handleNavigate = () => {
    const url = getGoogleMapsUrl(lat, lng, address);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleCall = () => {
    if (customerPhone) {
      window.location.href = `tel:${customerPhone}`;
    }
  };

  const handleMarkerDragEnd = () => {
    if (markerRef.current && onLocationChange) {
      const marker = markerRef.current;
      const position = marker.getLatLng();
      onLocationChange({ lat: position.lat, lng: position.lng });
    }
  };

  if (!hasCoordinates && !address) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-center text-gray-500">
          <MapPin className="w-8 h-8 mx-auto mb-2" />
          <p>No location provided</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden border ${className}`}>
      {/* Map */}
      <div style={{ height }}>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater center={center} zoom={zoom} />
          
          {hasCoordinates && (
            <Marker 
              position={[lat, lng]} 
              icon={customerIcon}
              draggable={draggable}
              ref={markerRef}
              eventHandlers={{
                dragend: handleMarkerDragEnd
              }}
            >
              <Popup>
                <div className="text-center">
                  <p className="font-semibold">{customerName || 'Customer Location'}</p>
                  {address && <p className="text-sm text-gray-600">{address}</p>}
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Address Display */}
      {address && (
        <div className="p-3 bg-gray-50 border-t">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700">{address}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {(showNavigation || showCallButton || onConfirmLocation) && (
        <div className="p-3 bg-white border-t flex gap-2 flex-wrap">
          {showNavigation && hasCoordinates && (
            <Button 
              onClick={handleNavigate}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              size="sm"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Navigate
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}
          
          {showCallButton && customerPhone && (
            <Button 
              onClick={handleCall}
              variant="outline"
              className="flex-1"
              size="sm"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Customer
            </Button>
          )}
          
          {onConfirmLocation && (
            <Button 
              onClick={onConfirmLocation}
              className="flex-1 bg-green-600 hover:bg-green-700"
              size="sm"
            >
              Confirm This Location
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Compact version for notifications/cards
export function MapPreviewCompact({ location, address, onNavigate }) {
  const lat = location?.lat || location?.latitude;
  const lng = location?.lng || location?.longitude;
  
  const handleNavigate = () => {
    const url = getGoogleMapsUrl(lat, lng, address);
    if (url) {
      window.open(url, '_blank');
    }
    if (onNavigate) onNavigate();
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
        <MapPin className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {address || `${lat?.toFixed(4)}, ${lng?.toFixed(4)}`}
        </p>
        <p className="text-xs text-gray-500">Tap to navigate</p>
      </div>
      <Button 
        onClick={handleNavigate}
        size="sm"
        className="bg-blue-600 hover:bg-blue-700"
      >
        <Navigation className="w-4 h-4" />
      </Button>
    </div>
  );
}
